import { useQuery } from "@tanstack/react-query";
import { EscalatedLabel } from "@/modules/orders/screens/EscalatedLabel";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { SelectionBar } from "@/components/table/SelectionBar";
import {
  inViewOf,
  selectionFacts,
  useSelection,
} from "@/components/table/selection";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { reportingNow } from "@/lib/reportClock";
import type { ShipmentRow } from "@/modules/orders/types";
import { projectShipmentRow } from "@/modules/orders/projections";
import { ShipmentMobileCard } from "@/modules/orders/screens/ShipmentRowParts";
import {
  HealthLabel,
  StageBadge,
} from "@/modules/orders/screens/TrackingParts";
import { needsAttention } from "@/modules/orders/tracking";
import type { SectionScreenProps } from "@/modules/types";

/**
 * Shipments across every order (#58, FR-ORD-6).
 *
 * One repository call per page. It used to fetch every order and every order's
 * detail to build these rows on the client, which the old comment here called
 * a bounded shortcut; against 428 real orders it was 429 requests per visit.
 * `listShipments` joins the order number and destination in the adapter and
 * filters, sorts and pages there.
 */
export function ShipmentsList({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  /**
   * Ticked shipments (#634), kept across pages and filters. No action is
   * named for shipments yet; the bar still gathers them and shows only them.
   */
  const selection = useSelection<ShipmentRow>((row) => row.id);

  const query = useQuery({
    queryKey: ["orders", "shipments", filter, sort, limit, cursor],
    queryFn: () => data.orders.listShipments({ filter, sort, limit, cursor }),
  });

  if (query.isPending) return <TableSkeleton />;

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DataTable
        fields={fields}
        rows={
          selection.viewing ? [...selection.rows.values()] : query.data.items
        }
        getRowId={(row) => row.id}
        project={projectShipmentRow}
        selection={{
          isSelected: (row) => selection.has(row.id),
          onToggle: (row, on) => {
            selection.set(row, on);
          },
          onTogglePage: (rows, on) => {
            selection.setMany(rows, on);
          },
          labelOf: (row) => `Select the shipment of ${row.orderNumber}`,
          active: selection.size > 0,
        }}
        sort={sort}
        onToggleSort={onToggleSort}
        // One status (#642): the stage as a badge, with the health beside it
        // only when it needs a look. "On track" on most rows was a third
        // coloured column saying nothing. A shipment never checked has neither.
        renderOverride={(field, row) => {
          if (field.name === "stage") {
            return row.stage ? (
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <StageBadge stage={row.stage} />
                {needsAttention(row.health) ? (
                  <HealthLabel health={row.health} />
                ) : null}
                {row.openEscalation ? <EscalatedLabel /> : null}
              </span>
            ) : undefined;
          }
          if (field.name === "health") {
            return row.health ? <HealthLabel health={row.health} /> : undefined;
          }
          return undefined;
        }}
        mobileRow={(row) => <ShipmentMobileCard row={row} />}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          selection.viewing ? undefined : (
            <Pagination
              page={query.data}
              limit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          )
        }
      />

      <SelectionBar
        selection={selection}
        {...selectionFacts({
          selection,
          pageRows: query.data.items,
          idOf: (row) => row.id,
          inView: inViewOf(projectShipmentRow, filter, reportingNow()),
        })}
        matching={query.data.total}
        noun="shipments"
      />
    </div>
  );
}
